/* Find duplicate SHEETS in the register — the same drawing appearing more than
   once, e.g. filed under a building AND left as a stray in the Unassigned folder.

   The identity is the SHEET, never the parent file: the same drawing is often
   uploaded twice from different combined PDFs, so the parent name/size/hash and
   the page index all differ. Two independent signals catch a duplicate, and they
   are UNIONED (either is enough), which is what makes it robust:

     • identical rendered CONTENT — a SHA-256 of the sheet's preview image. The
       render is deterministic, so the same drawing hashes the same even from a
       different parent PDF, and it catches copies whose sheet NUMBERS differ.
     • same NUMBER + TITLE across folders — catches copies whose previews were
       re-rendered slightly differently (different bytes) so their hashes don't
       match, which the content signal alone would miss.

   The one thing we must NOT do is merge two genuinely-different drawings that
   happen to share a number across two real buildings — so a number+title pair is
   only merged when it isn't already PROVEN different by two known, differing
   content hashes. Every candidate is shown with its preview + folder so the
   studio confirms before purging. */

import type { Deliverable } from "./types";

export type DupeConfidence = "content" | "title";
export interface DupeItem {
  id: string; code: string; title: string; building?: string;
  fileName?: string; size?: number; revisions: number; archived?: boolean;
  keep: boolean;
}
export interface DupeGroup {
  key: string; label: string; confidence: DupeConfidence; items: DupeItem[];
}
export interface DupeScan { groups: DupeGroup[]; compared: number; unreadable: number }

async function sha256(buf: ArrayBuffer): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
async function sha256Text(s: string): Promise<string> { return sha256(new TextEncoder().encode(s).buffer as ArrayBuffer); }

export async function hashFile(file: File): Promise<string | undefined> {
  try { return await sha256(await file.arrayBuffer()); } catch { return undefined; }
}

/* Content signature = hash of the rendered preview (identical drawings render
   identically). Falls back to the original file's hash + page. Cached per id. */
const sigCache = new Map<string, string | null>();
async function contentSig(d: Deliverable): Promise<string | null> {
  if (sigCache.has(d.id)) return sigCache.get(d.id)!;
  let v: string | null = null;
  const pu = d.previewUrl;
  if (pu) {
    if (pu.startsWith("data:")) { try { v = "c:" + await sha256Text(pu); } catch { v = null; } }
    else { try { const r = await fetch(pu, { credentials: "same-origin" }); if (r.ok) v = "c:" + await sha256(await r.arrayBuffer()); } catch { v = null; } }
  }
  if (!v && d.fileHash) v = `o:${d.fileHash}:${d.page ?? 1}`;
  sigCache.set(d.id, v);
  return v;
}

const norm = (c: string) => (c || "").trim().toUpperCase().replace(/\s+/g, "");
const normTitle = (t: string) => (t || "").trim().toLowerCase().replace(/\s+/g, " ");

function toItems(list: Deliverable[], keepAllAssigned: boolean): DupeItem[] {
  const items: DupeItem[] = list.map((d) => ({
    id: d.id, code: d.code, title: d.title, building: d.building, fileName: d.fileName,
    size: d.fileSize, revisions: (d.issues ?? []).length, archived: d.archived, keep: false,
  }));
  if (keepAllAssigned && items.some((i) => i.building)) {
    // safe title-match: keep EVERY filed (assigned) copy; only the building-less
    // strays are proposed for removal — a legitimately-filed sheet is never a remove.
    items.forEach((it) => (it.keep = !!it.building));
  } else {
    // identical content (or all strays): one keeper, prefer filed → most revs → id
    items.sort((a, b) =>
      (Number(!!a.archived) - Number(!!b.archived)) ||
      (Number(!a.building) - Number(!b.building)) ||
      (b.revisions - a.revisions) ||
      a.id.localeCompare(b.id));
    items[0].keep = true;
  }
  // keepers first, then by folder, for a readable list
  items.sort((a, b) => (Number(b.keep) - Number(a.keep)) || (a.building || "~~").localeCompare(b.building || "~~"));
  return items;
}

/** Scan the register for duplicate sheets. onProgress reports sheets compared. */
export async function findDuplicateGroups(
  dels: Deliverable[],
  onProgress?: (done: number, total: number) => void,
): Promise<DupeScan> {
  const sheets = dels.filter((d) => d.previewUrl || d.fileUrl);
  const n = sheets.length;

  // content signature for every sheet (bounded concurrency)
  const sig: (string | null)[] = new Array(n).fill(null);
  const CONC = 8;
  for (let i = 0; i < n; i += CONC) {
    const batch = sheets.slice(i, i + CONC);
    await Promise.all(batch.map(async (d, bi) => { sig[i + bi] = await contentSig(d); }));
    onProgress?.(Math.min(n, i + CONC), n);
  }

  const groups: DupeGroup[] = [];
  const used = new Set<number>();

  // 1) identical rendered content — definitive, across any folder or number
  const byContent = new Map<string, number[]>();
  sig.forEach((s, i) => { if (s) (byContent.get(s) ?? byContent.set(s, []).get(s)!).push(i); });
  for (const idxs of byContent.values()) {
    if (idxs.length < 2) continue;
    idxs.forEach((i) => used.add(i));
    const list = idxs.map((i) => sheets[i]);
    groups.push({ key: list.map((d) => d.id).sort().join(","), label: `${list[0].code} — ${list[0].title}`, confidence: "content", items: toItems(list, false) });
  }

  // 2) same number + title that involves an UNASSIGNED stray. This catches copies
  //    whose previews re-rendered to different bytes (so their content hashes
  //    don't match) WITHOUT falsely merging genuinely-different sheets that share
  //    a number across two real buildings — those never involve a building-less
  //    copy, and even here only the stray is ever proposed for removal.
  const byCT = new Map<string, number[]>();
  sheets.forEach((d, i) => { if (used.has(i)) return; const k = norm(d.code) + "|" + normTitle(d.title); (byCT.get(k) ?? byCT.set(k, []).get(k)!).push(i); });
  for (const idxs of byCT.values()) {
    if (idxs.length < 2) continue;
    if (!idxs.some((i) => !sheets[i].building)) continue;   // no stray → legit multi-building, leave it
    const list = idxs.map((i) => sheets[i]);
    groups.push({ key: list.map((d) => d.id).sort().join(","), label: `${list[0].code} — ${list[0].title}`, confidence: "title", items: toItems(list, true) });
  }

  groups.sort((a, b) => (b.items.length - a.items.length) || (a.confidence === b.confidence ? 0 : a.confidence === "content" ? -1 : 1));
  const unreadable = sig.filter((s) => !s).length;
  return { groups, compared: n, unreadable };
}
